import { useState, useEffect } from 'react'
import { toast } from 'react-hot-toast'
import AdminLayout from '../../../layouts/AdminLayout'
import authService from '@services/api/auth'
import apiClient from '@services/api/client'

function PersonalSecurityPage() {
  const [loading, setLoading] = useState(false)
  const [user, setUser] = useState(null)
  const [showChangePassword, setShowChangePassword] = useState(false)
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  })
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false)
  const [activeSessions, setActiveSessions] = useState([])
  const [loginHistory, setLoginHistory] = useState([])

  useEffect(() => {
    loadUserSecurity()
  }, [])

  const loadUserSecurity = async () => {
    setLoading(true)
    try {
      const adminUser = JSON.parse(localStorage.getItem('admin_user'))
      setUser(adminUser)

      // Charger les sessions actives
      try {
        const sessionsData = await apiClient.get(`/profile/sessions`)
        setActiveSessions(sessionsData.sessions || [])
      } catch {}

      // Charger l'historique de connexion
      try {
        const historyData = await apiClient.get(`/profile/login-history`)
        setLoginHistory(historyData.history || [])
      } catch {}

      // Charger le statut 2FA
      try {
        const twoFaData = await apiClient.get(`/profile/2fa/status`)
        if (twoFaData.enabled !== undefined) setTwoFactorEnabled(twoFaData.enabled)
      } catch {}
    } catch (error) {
      console.error('Erreur chargement sécurité:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleChangePassword = async (e) => {
    e.preventDefault()

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas')
      return
    }

    if (passwordForm.new_password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères')
      return
    }

    try {
      await authService.changePassword({
        current_password: passwordForm.current_password,
        password: passwordForm.new_password,
        password_confirmation: passwordForm.confirm_password
      })
      toast.success('Mot de passe modifié avec succès')
      setShowChangePassword(false)
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' })
    } catch (error) {
      toast.error(error.message || 'Erreur lors du changement de mot de passe')
    }
  }

  const handleToggle2FA = async () => {
    try {
      await apiClient.post('/profile/2fa/toggle', {
        client_id: user.client_id,
        enabled: !twoFactorEnabled
      })
      setTwoFactorEnabled(!twoFactorEnabled)
      toast.success(`Authentification à deux facteurs ${!twoFactorEnabled ? 'activée' : 'désactivée'}`)
    } catch {
      toast.error('Fonctionnalité bientôt disponible')
    }
  }

  const handleTerminateSession = async (sessionId) => {
    try {
      await apiClient.post('/profile/sessions/terminate', { session_id: sessionId })
      toast.success('Session terminée')
      loadUserSecurity()
    } catch (e) {
      toast.error(e.message || 'Erreur lors de la terminaison')
    }
  }

  const getDeviceIcon = (device) => {
    if (device?.toLowerCase().includes('mobile') || device?.toLowerCase().includes('android') || device?.toLowerCase().includes('ios')) {
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      )
    }
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    )
  }

  return (
    <AdminLayout>
      <div className="space-y-6 mt-10">
        {/* Header */}
        <div className="bg-white rounded-md border border-slate-200 p-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-gray-800">Ma Sécurité</h1>
              <p className="text-gray-500 mt-2 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Gérez vos paramètres de sécurité personnels
              </p>
            </div>
            <button
              onClick={loadUserSecurity}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#FFD700] to-[#F5C800] text-[#002A4E] rounded-md transition-all font-medium"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Actualiser
            </button>
          </div>
        </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F5B700]"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Colonne gauche - Paramètres principaux */}
          <div className="lg:col-span-2 space-y-6">
            {/* Changement de mot de passe */}
            <div className="bg-white rounded-md border border-slate-200">
              <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-[#002A4E]">Mot de passe</h2>
                  <p className="text-sm text-gray-600 mt-1">Modifiez votre mot de passe régulièrement</p>
                </div>
                <button
                  onClick={() => setShowChangePassword(!showChangePassword)}
                  className="text-[#B8860B] hover:text-[#B8860B] font-medium transition-colors"
                >
                  {showChangePassword ? 'Annuler' : 'Modifier'}
                </button>
              </div>
              
              {showChangePassword ? (
                <form onSubmit={handleChangePassword} className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Mot de passe actuel</label>
                    <div className="relative">
                      <input
                        type={showCurrentPassword ? "text" : "password"}
                        value={passwordForm.current_password}
                        onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                        placeholder="Entrez votre mot de passe actuel"
                        className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showCurrentPassword ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nouveau mot de passe</label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        value={passwordForm.new_password}
                        onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                        placeholder="Entrez votre nouveau mot de passe"
                        className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showNewPassword ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Confirmer le mot de passe</label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={passwordForm.confirm_password}
                        onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                        placeholder="Confirmez votre nouveau mot de passe"
                        className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showConfirmPassword ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-[#FFD700] hover:bg-[#F5C800] text-[#002A4E] font-semibold py-3 px-6 rounded-md transition-colors"
                  >
                    Enregistrer le nouveau mot de passe
                  </button>
                </form>
              ) : (
                <div className="p-6">
                  <div className="flex items-center gap-3 text-gray-600">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <p className="text-sm">Votre mot de passe est protégé</p>
                  </div>
                </div>
              )}
            </div>

            {/* Authentification à deux facteurs */}
            <div className="bg-white rounded-md border border-slate-200">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-xl font-semibold text-[#002A4E]">Authentification à deux facteurs (2FA)</h2>
                <p className="text-sm text-gray-600 mt-1">Ajoutez une couche de sécurité supplémentaire</p>
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-md">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-md flex items-center justify-center ${
                      twoFactorEnabled ? 'bg-green-100' : 'bg-gray-200'
                    }`}>
                      <svg className={`w-6 h-6 ${twoFactorEnabled ? 'text-green-600' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">
                        {twoFactorEnabled ? 'Activé' : 'Désactivé'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {twoFactorEnabled ? 'Votre compte est protégé' : 'Protection recommandée'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleToggle2FA}
                    className={`px-4 py-2 rounded-md font-medium transition-colors ${
                      twoFactorEnabled 
                        ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    {twoFactorEnabled ? 'Désactiver' : 'Activer'}
                  </button>
                </div>
              </div>
            </div>

            {/* Sessions actives */}
            <div className="bg-white rounded-md border border-slate-200">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-xl font-semibold text-[#002A4E]">Sessions actives</h2>
                <p className="text-sm text-gray-600 mt-1">Gérez vos appareils connectés</p>
              </div>
              <div className="p-6">
                {activeSessions.length > 0 ? (
                  <div className="space-y-3">
                    {activeSessions.map((session, index) => (
                      <div key={index} className="flex items-center justify-between p-4 border border-gray-100 rounded-md hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-blue-50 rounded-md flex items-center justify-center text-blue-600">
                            {getDeviceIcon(session.device)}
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">{session.device || 'Appareil inconnu'}</p>
                            <p className="text-sm text-gray-600">{session.location || 'Localisation inconnue'}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              Dernière activité: {new Date(session.last_activity).toLocaleString('fr-FR')}
                            </p>
                          </div>
                        </div>
                        {!session.is_current && (
                          <button
                            onClick={() => handleTerminateSession(session.id)}
                            className="text-red-600 hover:text-red-700 font-medium text-sm"
                          >
                            Terminer
                          </button>
                        )}
                        {session.is_current && (
                          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                            Session actuelle
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <p className="text-sm">Aucune session active</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Colonne droite - Historique */}
          <div className="space-y-6">
            {/* Historique de connexion */}
            <div className="bg-white rounded-md border border-slate-200">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-xl font-semibold text-[#002A4E]">Historique</h2>
                <p className="text-sm text-gray-600 mt-1">Vos 10 dernières connexions</p>
              </div>
              <div className="p-6">
                {loginHistory.length > 0 ? (
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {loginHistory.map((entry, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 rounded-md border border-gray-100">
                        <div className={`w-2 h-2 rounded-full mt-2 ${
                          entry.success ? 'bg-green-500' : 'bg-red-500'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800">
                            {entry.success ? 'Connexion réussie' : 'Échec de connexion'}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">{entry.ip_address}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(entry.timestamp).toLocaleString('fr-FR')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <p className="text-sm">Aucun historique</p>
                  </div>
                )}
              </div>
            </div>

            {/* Conseils de sécurité */}
            <div className="bg-gradient-to-br from-[#FFD700] to-[#F5C800] rounded-md p-6 text-[#002A4E]">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                Conseils de sécurité
              </h3>
              <ul className="space-y-2 text-sm text-white/90">
                <li>• Utilisez un mot de passe fort et unique</li>
                <li>• Activez l'authentification à deux facteurs</li>
                <li>• Ne partagez jamais votre mot de passe</li>
                <li>• Déconnectez-vous des appareils publics</li>
                <li>• Surveillez vos sessions actives</li>
              </ul>
            </div>
          </div>
        </div>
      )}
      </div>
    </AdminLayout>
  )
}

export default PersonalSecurityPage
